/** GET params on the sign-in page: ?repo=…&pat=…&branch=… */

/** Public demo repo for the sign-in sample link. */
export const SAMPLE_REPO = 'codenesium/DailyLifePlayground';

/** Demo PAT for codenesium/DailyLifePlayground (intentionally public for one-click demo sign-in). */
export const DEMO_SAMPLE_PAT =
  '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';

/** PAT for sample link; optional `VITE_SAMPLE_PAT` overrides at build time. */
export function samplePat(): string {
  const fromEnv = import.meta.env.VITE_SAMPLE_PAT?.trim();
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEMO_SAMPLE_PAT;
}

function sampleSignInQuery(): URLSearchParams {
  return new URLSearchParams({ repo: SAMPLE_REPO, pat: samplePat() });
}

/** Same query string, relative to the current origin (local or Netlify). */
export function sampleSignInHref(): string {
  return `?${sampleSignInQuery().toString()}`;
}

/** Absolute sample URL for the current deployment origin. */
export function sampleSignInAbsoluteUrl(origin = window.location.origin): string {
  const base = origin.replace(/\/$/, '');
  return `${base}/${sampleSignInHref()}`;
}

/** Public Netlify URL shown in docs/tooltips (uses configured PAT when built with env). */
export function sampleSignInNetlifyUrl(): string {
  const base = 'https://codenesium.netlify.app';
  return `${base}/${sampleSignInHref()}`;
}

/** URL safe to show on screen (token redacted). */
export function sampleSignInUrlForDisplay(origin = window.location.origin): string {
  const params = new URLSearchParams({ repo: SAMPLE_REPO, pat: '••••••••' });
  const base = origin.replace(/\/$/, '');
  return `${base}/?${params.toString()}`;
}

export type AuthUrlPrefill = {
  repo?: string;
  pat?: string;
  branch?: string;
};

/** True when the URL carries sign-in prefill intent (search or hash query). */
export function hasAuthUrlPrefill(): boolean {
  const p = readAuthUrlPrefill();
  return Boolean(p.repo || p.pat || p.branch);
}

function readUrlSearchParams(): URLSearchParams {
  const merged = new URLSearchParams();

  const fromSearch = window.location.search;
  if (fromSearch.length > 1) {
    for (const [k, v] of new URLSearchParams(fromSearch)) merged.set(k, v);
  }

  const hash = window.location.hash;
  const qIndex = hash.indexOf('?');
  if (qIndex >= 0) {
    for (const [k, v] of new URLSearchParams(hash.slice(qIndex + 1))) {
      if (!merged.has(k)) merged.set(k, v);
    }
  }

  return merged;
}

function pickParam(params: URLSearchParams, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const v = params.get(key);
    if (v != null && v.trim()) return v.trim();
  }
  return undefined;
}

export function readAuthUrlPrefill(): AuthUrlPrefill {
  const params = readUrlSearchParams();
  const out: AuthUrlPrefill = {};
  const repo = pickParam(params, 'repo', 'repository');
  const pat = pickParam(params, 'pat', 'token', 'github_pat', 'access_token');
  const branch = pickParam(params, 'branch');
  if (repo) out.repo = repo;
  if (pat) out.pat = pat;
  if (branch) out.branch = branch;
  return out;
}

/** Remove token from the address bar after prefill (history, referrers). */
export function stripPatFromUrl(): void {
  const url = new URL(window.location.href);
  let changed = false;

  if (url.searchParams.has('pat')) {
    url.searchParams.delete('pat');
    changed = true;
  }
  for (const alias of ['token', 'github_pat', 'access_token']) {
    if (url.searchParams.has(alias)) {
      url.searchParams.delete(alias);
      changed = true;
    }
  }

  if (url.hash.includes('?')) {
    const [hashPath, hashQuery] = url.hash.split('?');
    const hp = new URLSearchParams(hashQuery ?? '');
    for (const key of ['pat', 'token', 'github_pat', 'access_token']) {
      if (hp.has(key)) {
        hp.delete(key);
        changed = true;
      }
    }
    const qs = hp.toString();
    url.hash = qs ? `${hashPath}?${qs}` : hashPath;
  }

  if (!changed) return;

  const qs = url.searchParams.toString();
  const next = url.pathname + (qs ? `?${qs}` : '') + url.hash;
  window.history.replaceState({}, '', next);
}
